import { SearchResult } from "@/hooks/useSearch";
import { cn } from "@/lib/utils";
import {
  Calendar,
  CheckCircle,
  Clock,
  GraduationCap,
  MessageSquare,
  Phone,
  PlayCircle,
  Star,
  User,
  Video,
  XCircle,
} from "lucide-react";
import React from "react";

interface SearchResultItemProps {
  result: SearchResult;
  isSelected?: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

export default function SearchResultItem({
  result,
  isSelected = false,
  onClick,
  onMouseEnter,
}: SearchResultItemProps) {
  const isStudent = result.type === "student";
  const student = isStudent ? result.data : null;
  const session = !isStudent ? result.data : null;

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "inactive":
        return "bg-red-100 text-red-800 border-red-200";
      case "graduated":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "ended":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getSessionIcon = (sessionType: string) => {
    switch (sessionType?.toLowerCase()) {
      case "chat":
        return MessageSquare;
      case "voice":
        return Phone;
      case "video":
        return Video;
      default:
        return Calendar;
    }
  };

  const getSessionStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "ended":
        return CheckCircle;
      case "cancelled":
        return XCircle;
      case "active":
        return PlayCircle;
      default:
        return Clock;
    }
  };

  return (
    <div
      className={cn(
        "flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200",
        isSelected
          ? "bg-blue-50 border border-blue-200"
          : "hover:bg-gray-50 border border-transparent",
        "group"
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      {/* Icon/Avatar */} 
      <div className="flex-shrink-0">
        {isStudent ? (
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
            {student?.profilePicture ? (
              <img
                src={student.profilePicture}
                alt={result.title}
                className="w-10 h-10 object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
            )}
          </div>
        ) : (
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center">
            {React.createElement(getSessionIcon(session?.sessionType), {
              className: "w-5 h-5 text-green-600",
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2 mb-1">
          <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
            {result.title}
          </h3>

          {/* Status indicators */}
          {isStudent && student?.status && (
            <span
              className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full border",
                getStatusColor(student.status)
              )}
            >
              {student.status}
            </span>
          )}

          {!isStudent && session?.status && (
            <div className="flex items-center space-x-1">
              {React.createElement(getSessionStatusIcon(session.status), {
                className: cn(
                  "w-3 h-3",
                  session.status === "ended"
                    ? "text-green-500"
                    : session.status === "cancelled"
                    ? "text-red-500"
                    : session.status === "active"
                    ? "text-blue-500"
                    : "text-gray-500"
                ),
              })}
              <span
                className={cn(
                  "px-2 py-0.5 text-xs font-medium rounded-full border",
                  getStatusColor(session.status)
                )}
              >
                {session.status}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center text-xs text-gray-600 space-x-3">
          {/* Subtitle with icon */}
          <div className="flex items-center space-x-1">
            {isStudent ? (
              <GraduationCap className="w-3 h-3" />
            ) : (
              <Calendar className="w-3 h-3" />
            )}
            <span className="truncate">{result.subtitle}</span>
          </div>

          {/* Additional info */}
          {result.additionalInfo && (
            <>
              <span className="text-gray-300">•</span>
              <div className="flex items-center space-x-1">
                {!isStudent && session?.rating ? (
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                ) : (
                  <Clock className="w-3 h-3" />
                )}
                <span>{result.additionalInfo}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Type indicator */}
      <div className="flex-shrink-0">
        <span
          className={cn(
            "px-2 py-1 text-xs font-medium rounded-full",
            isStudent
              ? "bg-blue-100 text-blue-700"
              : "bg-green-100 text-green-700"
          )}
        >
          {isStudent ? "Student" : "Session"}
        </span>
      </div>
    </div>
  );
}
